import type { ReactNode } from 'react';

interface EmptyStateProps {
  /** Decorative glyph (emoji or short text). Hidden from assistive tech. */
  icon: string;
  /** Short heading rendered as a `<p>` (this is not a landmark). */
  title: string;
  /** Optional descriptive body line(s). */
  description?: string;
  /** Optional action — typically an `<a>` or `<button>` wrapped externally. */
  action?: ReactNode;
  /** Optional extra class for parent-level layout overrides. */
  className?: string;
}

/**
 * Friendly empty-state card used wherever a list has zero items.
 * Per V1.0_PLAN polish — replaces single-line text-only empty states with
 * a glyph + title + description + optional CTA. Same visual rhythm as
 * `.not-found-card` + `.verify-email-card` for shell consistency.
 *
 * a11y (#1375): rendered as `role="note"` — identifiable and navigable by
 * assistive tech, but NOT a live region. Multiple of these can coexist on a
 * single view (e.g. an empty DM list + an empty thread), so auto-announcing
 * each on load (`role="status"`) created a polite-live-region pile-up that
 * muddied screen-reader narration. Dedicated announcers (sends, saves, new
 * messages) keep `role="status"`; static placeholders do not.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div className={`empty-state ${className ?? ''}`} role="note">
      <span className="empty-state-icon" aria-hidden="true">
        {icon}
      </span>
      <p className="empty-state-title">{title}</p>
      {description && <p className="empty-state-description">{description}</p>}
      {action && <div className="empty-state-action">{action}</div>}
    </div>
  );
}
